import React, { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { cart, useCartCount } from "../cart";
import { productImageUrls, shopErrorMessage } from "../shopApi";
import type { Product, ShopConfig } from "../shopApi";
import { useShopCatalog } from "../useShopCatalog";
import { checkoutPanel } from "../checkout";
import CheckoutDialog from "../../../components/store_page/checkout_dialog";
import { isPrintProduct, lowestPrintCents } from "../../prints/printProducts";
import { formatCents } from "../shopApi";
import { PrintTile } from "../../prints/today/PrintTile";
import { FORMATS_BIG_FIRST, ORIGINAL_FORMATS, useToday } from "../../prints/today/useToday";
import { TODAY_PRINTS_PATH, todayPrintsPath } from "../../prints/paths";
import "../../prints/today/todayPrints.css";
import { AddedToastHost } from "./AddedToast";
import { addedToast, cardPrice, deliveryFacts } from "./ui";
import "./storefront.css";

export const StoreHead: React.FC<{ config: ShopConfig | null }> = ({ config }) => {
  const count = useCartCount();
  const facts = deliveryFacts(config);
  return (
    <>
      <header className="sf-head">
        <div>
          <div className="sf-title-row">
            <h1 className="sf-title">store</h1>
          </div>
          <p className="sf-sub">things made around n450s.com</p>
        </div>
        {count > 0 && (
          <button type="button" className="sf-cart-btn" onClick={checkoutPanel.open}>
            cart ({count})
          </button>
        )}
      </header>
      {facts.length > 0 && (
        <p className="sf-strip">
          {facts.map((f, i) => (
            <span key={f}>
              {i > 0 && <span aria-hidden="true"> · </span>}
              {f}
            </span>
          ))}
        </p>
      )}
    </>
  );
};

export const LegalLinks: React.FC<{ config: ShopConfig | null }> = ({ config }) => {
  const legal = config?.legal;
  const links = [
    { name: "terms", url: legal?.terms },
    { name: "privacy", url: legal?.privacy },
    { name: "returns", url: legal?.returns },
  ].filter((l): l is { name: string; url: string } => Boolean(l.url));
  if (!links.length) return null;
  return (
    <footer className="sf-legal">
      {links.map((l, i) => (
        <React.Fragment key={l.name}>
          {i > 0 && <span aria-hidden="true"> · </span>}
          <a href={l.url} target="_blank" rel="noopener noreferrer">
            {l.name}
          </a>
        </React.Fragment>
      ))}
    </footer>
  );
};

const ProductCard: React.FC<{ product: Product; index: number }> = ({ product, index }) => {
  const [first, second] = productImageUrls(product);
  const variants = product.variants ?? [];
  const quickAdd = product.available && variants.length <= 1;

  return (
    <li className="sf-card" style={{ "--i": index } as React.CSSProperties}>
      <Link to={`/marketplace/${product.id}`} className="sf-card-link">
        <div className={`sf-card-media ${second ? "has-second" : ""}`}>
          {first && <img src={first} alt={product.name} loading={index < 4 ? "eager" : "lazy"} />}
          {second && <img src={second} alt="" aria-hidden="true" loading="lazy" className="sf-card-second" />}
          {!product.available && <span className="sf-tag">sold out</span>}
        </div>
        <div className="sf-card-body">
          <h2 className="sf-card-name">{product.name}</h2>
          <p className="sf-card-price">{cardPrice(product)}</p>
        </div>
      </Link>
      {quickAdd && (
        <button
          type="button"
          className="sf-quick"
          aria-label={`Add ${product.name} to cart`}
          onClick={() => {
            cart.add(product.id, variants[0]?.id);
            addedToast.show(product.name);
          }}
        >
          +
        </button>
      )}
    </li>
  );
};

/** Under the store grid: today's prints as small tiles and a way to the collectibles page. */
const CollectiblesSection: React.FC<{ products: Product[] }> = ({ products }) => {
  const { data: day } = useToday();
  if (!day) return null;
  return (
    <section aria-labelledby="sf-cl-title">
      <hr className="sf-sep" />
      <div className="sf-cl-head">
        <div>
          <h2 id="sf-cl-title" className="tp-h2">
            collectibles
          </h2>
          <p className="tp-note">
            prints of today&apos;s art of the day, #{day.number}, from {formatCents(lowestPrintCents(products), "EUR")}. pre-order only for now.
          </p>
        </div>
        <Link to="/collectibles" className="sf-head-chip">
          all collectibles →
        </Link>
      </div>
      <ul className="sf-cl-grid">
        {FORMATS_BIG_FIRST.map((info, i) => (
          <PrintTile
            key={info.format}
            day={day}
            info={info}
            edition="print"
            products={products}
            to={todayPrintsPath(info.format)}
            index={i}
          />
        ))}
        <PrintTile
          day={day}
          info={ORIGINAL_FORMATS[0]}
          edition="original"
          products={products}
          to={`${TODAY_PRINTS_PATH}#tp-og-title`}
          index={FORMATS_BIG_FIRST.length}
        />
      </ul>
    </section>
  );
};

const StorefrontPage: React.FC = () => {
  const { config, products: catalog, loading, error, reload } = useShopCatalog();
  // AOTD prints live on /collectibles, not in the grid
  const products = catalog.filter((product) => !isPrintProduct(product.id));
  const count = useCartCount();
  const [searchParams] = useSearchParams();
  const cancelled = searchParams.get("checkout") === "cancelled";

  useEffect(() => {
    document.title = "NΛSOS Store";
  }, []);

  // back from a cancelled card payment: the cart is kept, so reopen the checkout
  useEffect(() => {
    if (cancelled && count > 0) checkoutPanel.open();
  }, [cancelled, count]);

  return (
    <div className="sf-page">
      <StoreHead config={config} />
      {cancelled && <p className="sf-notice">The payment was cancelled. Your cart is still here.</p>}

      {loading ? (
        <ul className="sf-grid" aria-busy="true" aria-label="Loading the store">
          {[0, 1, 2, 3].map((i) => (
            <li key={i} className="sf-card sf-skeleton">
              <div className="sf-card-media" />
              <div className="sf-card-body">
                <span />
                <span />
              </div>
            </li>
          ))}
        </ul>
      ) : error ? (
        <div className="sf-state">
          <p className="sf-state-title">Can't reach the store</p>
          <p>{shopErrorMessage(error)}</p>
          <button type="button" className="sf-link-btn" onClick={reload}>
            try again
          </button>
        </div>
      ) : products.length === 0 ? (
        <div className="sf-state">
          <p className="sf-state-title">Items will be added soon</p>
          <p>The infrastructure is ready. Now just wait for the merch to appear!</p>
        </div>
      ) : (
        <ul className="sf-grid">
          {products.map((product, index) => (
            <ProductCard key={product.id} product={product} index={index} />
          ))}
        </ul>
      )}

      <CollectiblesSection products={catalog} />

      <LegalLinks config={config} />
      <CheckoutDialog />
      <AddedToastHost />
    </div>
  );
};

export default StorefrontPage;
